// src/pages/QuestionBankPage.jsx
import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link, useParams } from 'react-router-dom'
import {
  LayoutGrid, Table as TableIcon, Plus, Edit, Trash2, Loader, Search,
  Mountain, MessageCircleQuestion, MessageCircleMore,
  ChevronDown, ChevronUp, ChevronLeft, Save
} from 'lucide-react'
import Select from 'react-select'
import { motion, AnimatePresence } from 'framer-motion'
import Drawer from '@/layout/Drawer'
import ConfirmModal from '@/widgets/ConfirmModal'

import {
  fetchExamBank, deleteQuestion, getLessonName, updateQuestion, updateLesson
} from '@/api/kuizy'
import {
  levelMapping, examTypeMapping, typeSummaryMapping,
  optionLabel, formatDateTime
} from '@/settings/master'
import ViewSwitcher from '../components/ui/ViewSwitcher'

/* options */
const optAll = { value: '', label: 'ทั้งหมด' }
const toOptsWithAll = obj => [optAll, ...Object.entries(obj).map(([k, v]) => ({ value: k, label: v }))]
const toOptsNoAll = obj => Object.entries(obj).map(([k, v]) => ({ value: k, label: v }))

const optionStyleOptions = [
  { value: 'thai', label: 'ก ข ค ง' },
  { value: 'alpha', label: 'A B C D' },
  { value: 'number', label: '1 2 3 4' }
]

const TABS = [
  { key: 'meta', label: 'จัดการเนื้อหาบทเรียน' },
  { key: 'bank', label: 'จัดการข้อสอบ' }
]

export default function QuestionBankPage() {
  // params: cid = content_id (UUID), lid = lesson_id (UUID)
  const { cid, lid } = useParams()
  const uid = localStorage.userId || '1'
  const nav = useNavigate()

  /* ---- tab ---- */
  const [tab, setTab] = useState('bank') // เริ่มที่ "จัดการข้อสอบ" ตามพฤติกรรมเดิม

  /* ---- lesson meta (แท็บ 1) ---- */
  const [meta, setMeta] = useState({
    lesson_name: '',
    difficult_level: '2',        // <<--
    exam_type: 'multipleChoice',
    type_summary: 'TF',
    type_summary_min: 1,
    type_summary_max: 4,
    option_style: 'thai'
  })
  const [savingLesson, setSavingLesson] = useState(false)
  const [confirmSaveLesson, setConfirmSaveLesson] = useState(false)

  /* ---- bank (แท็บ 2) ---- */
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [lessonName, setLsn] = useState('')

  const [loading, setLoad] = useState(false)
  const [view, setView] = useState('table')
  const [searchIn, setSI] = useState('')
  const [searchTxt, setST] = useState('')

  const [lvF, setLv] = useState(optAll)
  const [exF, setEx] = useState(optAll)
  const [sumF, setSum] = useState(optAll)

  const [open, setOpen] = useState(false)
  const [editItem, setEdit] = useState(null)
  const [saving, setSav] = useState(false)
  const [delQ, setDelQ] = useState(null)
  const [delLoad, setDL] = useState(false)
  const [alert, setAlert] = useState({ open: false, msg: '' })

  const [expanded, setExpanded] = useState(new Set())
  const toggleAns = id => {
    const s = new Set(expanded)
    s.has(id) ? s.delete(id) : s.add(id)
    setExpanded(s)
  }

  /* ---- Load lesson name ---- */
  useEffect(() => {
    if (!lid) return
    getLessonName(lid)
      .then(({ data }) => {
        const name = data?.lesson || data?.lesson_name || ''
        setLsn(name)
        setMeta(m => ({ ...m, lesson_name: name }))
      })
      .catch(() => { })
  }, [lid])

  /* ---- Fetch questions (only when tab === 'bank') ---- */
  const fetchData = useCallback(async () => {
    if (tab !== 'bank') return
    setLoad(true)
    try {
      const { data } = await fetchExamBank({
        user_id: uid,
        content_id: cid,
        lesson_id: lid,
        level: lvF.value || undefined,
        type_summary: sumF.value || undefined,
        exam_type: exF.value || undefined,
        search: searchTxt || undefined,
        page: 1,
        pageSize: 'all'
      })
      const list = Array.isArray(data?.data) ? data.data : []
      setRows(list)
      setTotal(parseInt(data?.total || 0, 10))

      // ใช้ข้อมูลบทเรียนจากแถวแรก (ถ้ามี) เติม meta เริ่มต้น
      if (list.length) {
        const f = list[0]
        setMeta(m => ({
          ...m,
          difficult_level: (f.difficult_level ?? f.level ?? m.difficult_level)?.toString(), // <<--
          exam_type: f.exam_type || m.exam_type,
          type_summary: f.type_summary || m.type_summary,
          option_style: f.option_style || m.option_style
        }))
      }
    } catch {
      setRows([])
      setTotal(0)
    } finally {
      setLoad(false)
    }
  }, [tab, uid, cid, lid, lvF, exF, sumF, searchTxt])

  useEffect(() => { fetchData() }, [fetchData])

  /* ---- Edit helpers ---- */
  const startEdit = q => { setEdit(JSON.parse(JSON.stringify(q))); setOpen(true) }

  const updOption = (idx, field, val) => {
    const key = Object.keys(editItem.answer[idx])[0]
    const entry = editItem.answer[idx][key]
    const isObj = typeof entry === 'object'
    const newEntry =
      field === 'desc'
        ? (isObj ? { ...(entry || {}), description: val } : val)
        : { ...(isObj ? entry : { description: entry || '' }), points: val }
    const newAns = editItem.answer.map((a, i) => i === idx ? { [key]: newEntry } : a)
    setEdit(p => ({ ...p, answer: newAns }))
  }

  const toggleCorrect = key => {
    const isSingle = editItem.type_summary === 'TF'
    let corr = Array.isArray(editItem.correct) ? [...editItem.correct] : []
    if (corr.includes(key)) {
      corr = corr.filter(k => k !== key)
    } else {
      corr = isSingle ? [key] : [...corr, key]
    }
    if (corr.length === 0 && editItem.type_summary !== 'PO') return
    setEdit(p => ({ ...p, correct: corr }))
  }

  const saveEdit = async () => {
    if (!editItem) return
    setSav(true)
    try {
      await updateQuestion({
        id: editItem.id,
        question: editItem.question,
        answer: editItem.answer,
        correct: editItem.correct,
        option_style: editItem.option_style,
        content_id: cid,
        lesson_id: lid,
        lesson_name: lessonName || editItem.lesson_name || ''
      })
      await fetchData()
      setOpen(false)
    } catch {
      setAlert({ open: true, msg: 'บันทึกไม่สำเร็จ' })
    } finally {
      setSav(false)
    }
  }

  const confirmDel = async () => {
    if (!delQ) return
    setDL(true)
    try {
      await deleteQuestion(delQ.id)
      await fetchData()
    } finally {
      setDL(false)
      setDelQ(null)
    }
  }

  /* ---- Save lesson meta ---- */
  const saveLessonMeta = async () => {
    setConfirmSaveLesson(false)
    if (!meta.lesson_name.trim()) {
      setAlert({ open: true, msg: 'กรุณากรอกชื่อบทเรียน' })
      return
    }
    setSavingLesson(true)
    try {
      await updateLesson({
        id: lid,                 // ✅ ต้องมี
        content_id: cid,         // ✅ ใช้ uuid
        lesson_name: meta.lesson_name.trim(),
        difficult_level: meta.difficult_level,            // <<--
        exam_type: meta.exam_type,
        type_summary: meta.type_summary,
        type_summary_min: Number(meta.type_summary_min) || 1,
        type_summary_max: Number(meta.type_summary_max) || 4,
        option_style: meta.option_style
      })
      setLsn(meta.lesson_name.trim())
      setAlert({ open: true, msg: 'บันทึกบทเรียนเรียบร้อย' })
    } catch {
      setAlert({ open: true, msg: 'บันทึกบทเรียนไม่สำเร็จ' })
    } finally {
      setSavingLesson(false)
    }
  }


  const fadeUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 }, transition: { duration: .35 } }

  return (
    <motion.section {...fadeUp} className="flex flex-col gap-6 p-4 sm:p-6">

      {/* ───────── Header ───────── */}
      <motion.header
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: .35, delay: .05 }}
        className="flex flex-wrap items-end gap-3 justify-between"
      >
        <h1 className="flex items-center gap-2 text-lg font-semibold cursor-pointer" onClick={() => tab === 'bank' ? fetchData() : null}>
          <Link to={`/content/${encodeURIComponent(cid)}/lessons/`} className="text-primary font-bold text-base hover:underline mr-2">
            <ChevronLeft size={30} />
          </Link>
          <LayoutGrid size={20} /> {lessonName || meta.lesson_name || '-'}
          {tab === 'bank' && <> : คลังข้อสอบ ({total})</>}
        </h1>

        <div className="flex flex-wrap gap-2">
          {/* ✅ ปุ่มสลับมุมมอง แสดงเฉพาะแท็บ "จัดการข้อสอบ" */}
          {tab === 'bank' && (
            <ViewSwitcher view={view} setView={setView} />
          )}

          {tab === 'bank' ? (
            <>
              <button
                onClick={() => nav(`/content/${cid}/lessons/${lid}/create-exam`,
                  { state: { content_id: cid, lesson_id: lid, lessonName } })}
                className="inline-flex items-center gap-1 bg-primary hover:bg-secondary text-white rounded-lg px-3 py-2 text-sm"
              >
                <Plus size={16} /> เพิ่มข้อสอบ
              </button>
              <button
                onClick={() => nav(`/content/${cid}/tests/new`)}
                className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"
              >
                <Plus size={16} /> สร้างชุดข้อสอบ
              </button>
            </>
          ) : (
            <button
              onClick={() => setConfirmSaveLesson(true)}
              disabled={savingLesson}
              className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-lg px-3 py-2 text-sm disabled:opacity-60"
            >
              <Save size={16} /> {savingLesson ? 'กำลังบันทึก…' : 'บันทึกบทเรียน'}
            </button>
          )}
        </div>
      </motion.header>

      {/* ───────── Tabs ───────── */}
      <div className="flex border-b border-gray-200">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={[
              'px-4 py-2 -mb-px text-sm font-semibold border-b-2',
              tab === t.key ? 'text-primary border-primary' : 'text-gray-500 border-transparent hover:text-primary'
            ].join(' ')}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ───────── Tab: META ───────── */}
      {tab === 'meta' && (
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
          transition={{ duration: .25 }}
          className="bg-white rounded-xl shadow-md p-4 sm:p-6 space-y-4"
        >
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className="text-sm">ชื่อบทเรียน <span className="text-red-500 font-bold">*</span></label>
              <input
                value={meta.lesson_name}
                onChange={e => setMeta(m => ({ ...m, lesson_name: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                placeholder="เช่น บทที่ 1 ฟิสิกส์พื้นฐาน"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1 text-sm"><Mountain size={16} /> ระดับความยาก</label>
              <Select
                classNamePrefix="rs"
                options={Object.entries(levelMapping).map(([k, v]) => ({ value: k, label: v }))}
                value={Object.entries(levelMapping)
                  .map(([k, v]) => ({ value: k, label: v }))
                  .find(o => o.value === meta.difficult_level)}
                onChange={o => setMeta(m => ({ ...m, difficult_level: o?.value || '2' }))}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1 text-sm"><MessageCircleQuestion size={16} /> ประเภทข้อสอบ</label>
              <Select
                classNamePrefix="rs"
                options={toOptsNoAll(examTypeMapping)}
                value={toOptsNoAll(examTypeMapping).find(o => o.value === meta.exam_type)}
                onChange={o => setMeta(m => ({ ...m, exam_type: o?.value || 'multipleChoice' }))}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="flex items-center gap-1 text-sm"><MessageCircleMore size={16} /> ประเภทคำตอบ</label>
              <Select
                classNamePrefix="rs"
                options={toOptsNoAll(typeSummaryMapping)}
                value={toOptsNoAll(typeSummaryMapping).find(o => o.value === meta.type_summary)}
                onChange={o => setMeta(m => ({ ...m, type_summary: o?.value || 'TF' }))}
              />
            </div>
            {/* <div className="flex flex-col gap-1">
              <label className="text-sm">จำนวนคำตอบขั้นต่ำ</label>
              <input
                type="number"
                min={1}
                value={meta.type_summary_min}
                onChange={e => setMeta(m => ({ ...m, type_summary_min: Number(e.target.value) }))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm">จำนวนคำตอบสูงสุด</label>
              <input
                type="number"
                min={1}
                value={meta.type_summary_max}
                onChange={e => setMeta(m => ({ ...m, type_summary_max: Number(e.target.value) }))}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
              />
            </div> */}
            {/* <div className="flex flex-col gap-1">
              <label className="text-sm">รูปแบบตัวเลือก</label>
              <Select
                classNamePrefix="rs"
                options={optionStyleOptions}
                value={optionStyleOptions.find(o => o.value === meta.option_style)}
                onChange={o => setMeta(m => ({ ...m, option_style: o?.value || 'thai' }))}
              />
            </div> */}
          </div>
        </motion.div>
      )}

      {/* ───────── Tab: BANK ───────── */}
      {tab === 'bank' && (
        <>
          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .35, delay: .05 }}
            className="flex flex-wrap items-end gap-4 bg-white rounded-xl shadow-md p-4"
          >
            <div className="flex flex-col gap-1 w-52">
              <label className="flex items-center gap-1 text-sm"><Mountain size={16} /> ระดับความยาก</label>
              <Select classNamePrefix="rs" options={toOptsWithAll(levelMapping)} value={lvF} onChange={setLv} />
            </div>
            <div className="flex flex-col gap-1 w-52">
              <label className="flex items-center gap-1 text-sm"><MessageCircleQuestion size={16} /> ประเภทข้อสอบ</label>
              <Select classNamePrefix="rs" options={toOptsWithAll(examTypeMapping)} value={exF} onChange={setEx} />
            </div>
            <div className="flex flex-col gap-1 w-52">
              <label className="flex items-center gap-1 text-sm"><MessageCircleMore size={16} /> ประเภทคำตอบ</label>
              <Select classNamePrefix="rs" options={toOptsWithAll(typeSummaryMapping)} value={sumF} onChange={setSum} />
            </div>
            <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
              <label className="flex items-center gap-1 text-sm"><Search size={16} /> ค้นหา</label>
              <div className="relative">
                <input
                  value={searchIn}
                  onChange={e => setSI(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && setST(searchIn)}
                  className="w-full border border-gray-300 rounded-lg px-10 py-2 text-sm focus:ring-2 focus:ring-primary"
                  placeholder="ค้นหาคำถาม / รหัสข้อสอบ"
                />
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
              </div>
            </div>
            <button
              onClick={() => setST(searchIn)}
              className="inline-flex items-center gap-1 bg-secondary hover:bg-primary text-white rounded-lg px-3 py-2 text-sm"
            >
              <Search size={16} /> ค้นหา
            </button>
          </motion.div>

          {/* Table view */}
          {view === 'table' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .35, delay: .1 }}
              className="bg-white rounded-xl shadow-md overflow-x-auto">
              <table className="min-w-[880px] w-full text-sm">
                <thead className="bg-gray-100 text-gray-600 whitespace-nowrap">
                  <tr>
                    <th className="py-2 px-3 w-14 text-left">#</th>
                    <th className="py-2 px-3 text-left">คำถาม</th>
                    <th className="py-2 px-3 text-right">ระดับความยาก</th>
                    <th className="py-2 px-3 text-right">ประเภทข้อสอบ</th>
                    <th className="py-2 px-3 text-right">ประเภทคำตอบ</th>
                    <th className="py-2 px-3 text-right hidden lg:table-cell">สร้างเมื่อ</th>
                    <th className="py-2 px-3 text-right hidden lg:table-cell">แก้ไขล่าสุด</th>
                    <th className="py-2 px-3 w-24 text-right">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="py-6 text-center"><Loader className="animate-spin" /></td></tr>
                  ) : rows.length === 0 ? (
                    <tr><td colSpan={8} className="py-6 text-center text-gray-500">ไม่พบข้อสอบ</td></tr>
                  ) : rows.map((q, i) => {
                    const isEx = expanded.has(q.id)
                    return (
                      <tr key={q.id} className="odd:bg-white even:bg-gray-50 align-top">
                        <td className="py-2 px-3">{i + 1}</td>
                        <td className="py-2 px-3">
                          <div className="flex items-start gap-2">
                            <button onClick={() => toggleAns(q.id)} className="text-primary mt-[2px]">
                              {isEx ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            <span className="whitespace-pre-line break-words">{q.question}</span>
                          </div>
                          {isEx && (
                            <ul className="mt-2 ml-6 list-disc text-xs space-y-1">
                              {(q.answer || []).map((a, idx) => {
                                const k = Object.keys(a)[0]
                                const v = a[k]
                                const desc = typeof v === 'object' ? v.description : v
                                return (
                                  <li key={k} className={Array.isArray(q.correct) && q.correct.includes(k) ? 'text-green-700' : ''}>
                                    {optionLabel(idx, q.option_style || 'thai')}. {desc}
                                  </li>
                                )
                              })}
                            </ul>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{levelMapping[q.level] || levelMapping[q.difficult_level] || '-'}</td>
                        <td className="py-2 px-3 text-right">{examTypeMapping[q.exam_type] || '-'}</td>
                        <td className="py-2 px-3 text-right">{typeSummaryMapping[q.type_summary] || '-'}</td>
                        <td className="py-2 px-3 text-right hidden lg:table-cell">{formatDateTime(q.created_at)}</td>
                        <td className="py-2 px-3 text-right hidden lg:table-cell">{formatDateTime(q.updated_at || q.created_at)}</td>
                        <td className="py-2 px-3 text-right space-x-2 whitespace-nowrap">
                          <button onClick={() => startEdit(q)} className="text-primary hover:text-primary/80"><Edit size={16} /></button>
                          <button onClick={() => setDelQ(q)} className="text-red-600 hover:text-red-500"><Trash2 size={16} /></button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </motion.div>
          )}

          {/* Card view */}
          {view === 'card' && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .35, delay: .1 }}
              className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {loading ? (
                <div className="col-span-full flex justify-center py-12"><Loader className="animate-spin" /></div>
              ) : rows.length === 0 ? (
                <div className="col-span-full text-center text-gray-500 py-12">ไม่พบข้อสอบ</div>
              ) : rows.map((q, i) => {
                const isEx = expanded.has(q.id)
                return (
                  <motion.div key={q.id} whileHover={{ y: -4 }}
                    className="bg-white rounded-2xl shadow-md p-6 flex flex-col gap-4">
                    <div className="flex items-start gap-2">
                      <button onClick={() => toggleAns(q.id)} className="text-primary mt-[2px]">
                        {isEx ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </button>
                      <p className="font-semibold whitespace-pre-line break-words flex-1">
                        {i + 1}. {q.question}
                      </p>
                      <button onClick={() => startEdit(q)} className="text-primary hover:text-primary/80"><Edit size={18} /></button>
                    </div>

                    {isEx && (
                      <ul className="ml-6 list-disc text-xs space-y-1">
                        {(q.answer || []).map((a, idx) => {
                          const k = Object.keys(a)[0]
                          const v = a[k]
                          const desc = typeof v === 'object' ? v.description : v
                          return (
                            <li key={k} className={Array.isArray(q.correct) && q.correct.includes(k) ? 'text-green-700' : ''}>
                              {optionLabel(idx, q.option_style || 'thai')}. {desc}
                            </li>
                          )
                        })}
                      </ul>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-neutral-100 rounded-lg p-3 flex flex-col items-center">
                        <Mountain size={18} className="text-secondary" />
                        <span className="text-[10px] text-gray-600 mt-1">ระดับความยาก</span>
                        <strong className="text-md text-center">{levelMapping[q.level] || levelMapping[q.difficult_level] || '-'}</strong>
                      </div>
                      <div className="bg-neutral-100 rounded-lg p-3 flex flex-col items-center">
                        <MessageCircleQuestion size={18} className="text-secondary" />
                        <span className="text-[10px] text-gray-600 mt-1">ประเภทข้อสอบ</span>
                        <strong className="text-md text-center">{examTypeMapping[q.exam_type] || '-'}</strong>
                      </div>
                      <div className="bg-neutral-100 rounded-lg p-3 flex flex-col items-center">
                        <MessageCircleMore size={18} className="text-secondary" />
                        <span className="text-[10px] text-gray-600 mt-1">ประเภทคำตอบ</span>
                        <strong className="text-md text-center">{typeSummaryMapping[q.type_summary] || '-'}</strong>
                      </div>
                    </div>

                    <div className="flex items-end justify-between">
                      <div className="text-xs text-gray-500">
                        <p>สร้างเมื่อ {formatDateTime(q.created_at)}</p>
                        <p>แก้ไขล่าสุด {formatDateTime(q.updated_at || q.created_at)}</p>
                      </div>
                      <button onClick={() => setDelQ(q)} className="text-red-600 hover:text-red-500"><Trash2 size={18} /></button>
                    </div>
                  </motion.div>
                )
              })}
            </motion.div>
          )}
        </>
      )}

      {/* ───────── Drawer: Edit Question ───────── */}
      <Drawer open={open} onClose={() => setOpen(false)} title="แก้ไขข้อสอบ">
        {editItem && (
          <>
            <label className="text-sm font-medium flex gap-2 mb-2">
              <MessageCircleQuestion size={16} /> คำถาม
            </label>
            <textarea
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm h-24 w-full focus:ring-2 focus:ring-primary mb-6"
              value={editItem.question}
              onChange={e => setEdit({ ...editItem, question: e.target.value })}
            />

            <label className="text-sm font-medium mt-2 flex gap-2 mb-2">
              <MessageCircleMore size={16} /> ตัวเลือก
            </label>
            <ul className="space-y-2">
              {(editItem.answer || []).map((opt, idx) => {
                const key = Object.keys(opt)[0]
                const val = opt[key]
                const isPO = editItem.type_summary === 'PO'
                const isCorr = Array.isArray(editItem.correct) && editItem.correct.includes(key)
                const desc = typeof val === 'object' ? (val.description || '') : (val || '')
                const pts = typeof val === 'object' ? (Number(val.points || 0)) : 0
                return (
                  <li key={idx} className="flex gap-2 items-start">
                    <span className="mt-2 font-semibold">{optionLabel(idx, editItem.option_style || 'thai')}</span>
                    <textarea
                      className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary"
                      value={desc}
                      onChange={e => updOption(idx, 'desc', e.target.value)}
                    />
                    {isPO && (
                      <input
                        type="number"
                        className="w-24 border border-gray-300 rounded-lg px-2 py-1 text-sm focus:ring-2 focus:ring-primary"
                        value={pts}
                        onChange={e => updOption(idx, 'pts', Number(e.target.value))}
                      />
                    )}
                    {!isPO && (
                      <input
                        type="checkbox"
                        checked={isCorr}
                        onChange={() => toggleCorrect(key)}
                        className="mt-3"
                      />
                    )}
                  </li>
                )
              })}
            </ul>

            <button
              disabled={saving}
              onClick={saveEdit}
              className="bg-primary hover:bg-secondary text-white rounded-lg px-4 py-2 mt-4 disabled:opacity-50"
            >
              {saving ? <Loader className="animate-spin" size={16} /> : 'บันทึก'}
            </button>
          </>
        )}
      </Drawer>

      {/* ───────── Confirm & Alert ───────── */}
      <AnimatePresence>
        {confirmSaveLesson && (
          <ConfirmModal
            type="confirm"
            title="ยืนยันการบันทึก"
            message="ต้องการบันทึกการเปลี่ยนแปลงของบทเรียนหรือไม่?"
            onConfirm={saveLessonMeta}
            onCancel={() => setConfirmSaveLesson(false)}
          />
        )}

        {delQ && (
          <ConfirmModal
            type="confirm"
            title="ยืนยันการลบ"
            message="ลบคำถามนี้ ?"
            onConfirm={confirmDel}
            onCancel={() => setDelQ(null)}
            confirmDisabled={delLoad}
          />
        )}

        {alert.open && (
          <ConfirmModal
            type="alert"
            title="แจ้งเตือน"
            message={alert.msg}
            onClose={() => setAlert({ open: false, msg: '' })}
          />
        )}
      </AnimatePresence>
    </motion.section>
  )
}
